import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import QuestionnaireV2RefPage from '@/app/components/questionnaireV2/components/questionnaireV2Ref.page';
import { useGetQuestionnaire } from '@/app/services/questionnaireV2.service';
import useVisitorSession from '@/hooks/useVisitorSession';
import useOnMount from '@/hooks/useOnMount';
import { useSetIntercomIsVisible } from '@/queries/layout.queries';
import { QuizletVersionConfig } from './quizlet-config';
import { ROUTE_PAYMENT } from '@/constants/routes';
import useAnalytics from '@/hooks/use-analytics';

interface QuizletUnifiedWrapperProps {
  config: QuizletVersionConfig;
}

export default function QuizletUnifiedWrapper({ config }: QuizletUnifiedWrapperProps) {
  const navigate = useNavigate();
  const setIntercomIsVisible = useSetIntercomIsVisible();
  const { initializeSession } = useVisitorSession();
  const [isSessionReady, setIsSessionReady] = useState(false);
  const analytics = useAnalytics();

  // Only fetch questionnaire after session is initialized
  const { data: questionnaire, isLoading, isError } = useGetQuestionnaire(config.uuid, isSessionReady);
  // Hide intercom
  useOnMount(() => {
    setIntercomIsVisible.mutate(false);
    return () => {
      setIntercomIsVisible.mutate(true);
    };
  });

  // Initialize visitor session and mark as ready when complete
  useEffect(() => {
    initializeSession().then((session) => {
      if (session) {
        setIsSessionReady(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Handle completion - navigate to version-specific results
  const handleComplete = () => {
    navigate(ROUTE_PAYMENT);
  };

  // Analytics handlers for questionnaire events
  const onPageViewed = useCallback(
    (slug: string) => {
      analytics.callTrialPrequelPageViewed(slug, config.version);
    },
    [analytics, config.version],
  );
  const onAnswerSubmitted = useCallback(
    (slug: string, answerValue: string) => {
      analytics.callTrialPrequelAnswerSubmitted(slug, answerValue, config.version);
    },
    [analytics, config.version],
  );
  const analyticsHandlers = useMemo(
    () => ({ onPageViewed, onAnswerSubmitted }),
    [onPageViewed, onAnswerSubmitted],
  );

  // Single loading screen for all initialization stages
  if (!isSessionReady || isLoading || !questionnaire) {
    if (isError && !isLoading && !questionnaire) {
      return (
        <div className="flex h-screen w-screen items-center justify-center bg-black">
          <div className="text-lg text-white">Failed to load questionnaire. Please refresh the page.</div>
        </div>
      );
    }
    return <div className="h-screen w-screen bg-white" />;
  }

  // Use the existing QuestionnaireV2RefPage with onComplete callback
  return <QuestionnaireV2RefPage questionnaire={questionnaire} onComplete={handleComplete} analyticsHandlers={analyticsHandlers} />;
}
